import { useState, useEffect, useCallback } from 'react';
import { getGoogleToken, isGoogleConnected } from '../utils/googleAuth';

const useGoogleCalendarEvents = (dateRange = null) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize Google API
  const initializeGapi = useCallback(async () => {
    if (window.gapi && window.gapi.client) {
      return true;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = async () => {
        try {
          // ✅ Add fallback on failed gapi.client.load or .init calls
          await window.gapi.load('client', async () => {
            try {
              await window.gapi.client.init({
                apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
                discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
              });
              resolve(true);
            } catch (initError) {
              console.error('Failed to initialize Google API client:', initError);
              reject(initError);
            }
          });
        } catch (loadError) {
          console.error('Failed to load Google API client:', loadError);
          reject(loadError);
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google API script'));
      };
      document.head.appendChild(script);
    });
  }, []);

  // Fetch events from Google Calendar
  const fetchEvents = useCallback(async (startDate = null, endDate = null) => {
    if (!isGoogleConnected()) {
      setError('Google account not connected');
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      await initializeGapi();
      
      // ✅ Ensure token is retrieved correctly before API call
      const token = getGoogleToken();
      if (!token || typeof token !== 'string') {
        throw new Error('No valid Google token available');
      }

      // Set the access token
      window.gapi.client.setToken({ access_token: token });

      // Calculate date range - default to next 30 days
      const timeMin = startDate ? new Date(startDate) : new Date();
      const timeMax = endDate ? new Date(endDate) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const response = await window.gapi.client.calendar.events.list({
        calendarId: 'primary',
        timeMin: timeMin.toISOString(),
        timeMax: timeMax.toISOString(),
        showDeleted: false,
        singleEvents: true,
        maxResults: 100,
        orderBy: 'startTime',
      });

      const fetchedEvents = response.result.items || [];
      setEvents(fetchedEvents);
      return fetchedEvents;
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError(err.message || 'Failed to fetch calendar events');
      return [];
    } finally {
      setLoading(false);
    }
  }, [initializeGapi]);

  // Refresh events
  const refreshEvents = useCallback(() => {
    if (dateRange) {
      return fetchEvents(dateRange.start, dateRange.end);
    } else {
      return fetchEvents();
    }
  }, [fetchEvents, dateRange]);

  // Auto-fetch on mount and when dateRange changes
  useEffect(() => {
    if (isGoogleConnected()) {
      refreshEvents();
    }
  }, [refreshEvents]);

  // Create a new calendar event
  const createEvent = useCallback(async (eventData) => {
    if (!isGoogleConnected()) {
      throw new Error('Google account not connected');
    }

    try {
      await initializeGapi();
      
      const token = getGoogleToken();
      if (!token || typeof token !== 'string') {
        throw new Error('No valid Google token available');
      }

      window.gapi.client.setToken({ access_token: token });

      // ✅ Add guard on eventData format before sending insert
      if (!eventData || typeof eventData !== 'object') {
        throw new Error('Invalid event data provided');
      }

      // Validate required fields
      if (!eventData.summary && !eventData.start && !eventData.end) {
        throw new Error('Event must have summary, start, and end times');
      }

      const response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: eventData,
      });

      // Refresh events after creating
      await refreshEvents();
      
      return response.result;
    } catch (err) {
      console.error('Error creating calendar event:', err);
      throw err;
    }
  }, [initializeGapi, refreshEvents]);

  return {
    events,
    loading,
    error,
    fetchEvents,
    refreshEvents,
    createEvent,
    isConnected: isGoogleConnected(),
  };
};

export default useGoogleCalendarEvents;
